import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import "./PatchCompanyPolicy.css";

import { apiCall } from "../api/apiCall";
import Navbar from "../components/Navbar/Navbar";
import TopHeader from "../components/Navbar/TopHeader";
import { CompanyPolicy } from "../models/company_policy";
import { CategoryLabels } from "../utils/categoryLabels";
import generateToastContainer from "../utils/ToastContainer";
import { useTranslation } from "../i18n/UseTranslation";
// Import du composant de dialogue
import ConfirmDialog from "../components/Navbar/ConfirmLogoutDialog";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function normalizeCategoryKey(itemType: string) {
  return itemType
    .toLowerCase()
    .replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

type ToggleItemProps = {
  label: string;
  value: boolean;
  onClick: () => void;
  disabled?: boolean;
};

function ToggleItem({
  label,
  value,
  onClick,
  disabled = false,
}: ToggleItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`policy-toggle-card ${value ? "active" : "inactive"}`}
      aria-pressed={value}
    >
      <span className={`policy-toggle-label ${value ? "active" : "inactive"}`}>
        {label}
      </span>

      <span
        className={`policy-toggle-switch ${value ? "active" : "inactive"}`}
        aria-hidden="true"
      >
        <span
          className={`policy-toggle-dot ${value ? "active" : "inactive"}`}
        />
      </span>
    </button>
  );
}

function PatchCompanyPolicy() {
  const { companyUuid, policyUuid } = useParams<{
    companyUuid: string;
    policyUuid: string;
  }>();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const translations = t.companyPolicyForm;
  const categoryTypes = t.categoryTypes;

  const [formData, setFormData] = useState<Partial<CompanyPolicy>>({});

  // État pour la modale de sauvegarde
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);

  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const { data, isLoading, isError } = useQuery<CompanyPolicy, Error>({
    queryKey: ["policy", companyUuid, policyUuid],
    queryFn: () =>
      apiCall<CompanyPolicy>(
        `/companies/${companyUuid}/policies/${policyUuid}`,
      ),
    enabled: Boolean(companyUuid && policyUuid) && isAuthReady,
  });

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (updatedFields: Partial<CompanyPolicy>) =>
      apiCall(`/companies/${companyUuid}/policies/${policyUuid}`, {
        method: "PATCH",
        body: JSON.stringify(updatedFields),
      }),

    onSuccess: () => {
      toast.success(translations.updateSuccess);

      queryClient.invalidateQueries({
        queryKey: ["fetchCompanyPolicies", companyUuid],
      });

      navigate(-1);
    },

    onError: (error: Error) => {
      toast.error(
        translations.updateError?.replace("{message}", error.message) ||
          `Erreur: ${error.message}`,
      );
    },
  });

  const handleToggle = (field: keyof CompanyPolicy) => {
    setFormData((previous) => ({
      ...previous,
      [field]: !previous[field],
    }));
  };

  const handleChange = (
    field: keyof CompanyPolicy,
    value: string | boolean,
  ) => {
    const numericField =
      field === "spending_limit" || field === "max_amount_without_receipt";

    const finalValue = numericField
      ? value === ""
        ? null
        : Number(value)
      : value;

    setFormData((previous) => ({
      ...previous,
      [field]: finalValue,
    }));
  };

  const categoryItemType = formData.category?.item_type;

  const normalizedCategoryKey = categoryItemType
    ? (normalizeCategoryKey(categoryItemType) as keyof typeof categoryTypes)
    : null;

  const categoryName = normalizedCategoryKey
    ? (categoryTypes[normalizedCategoryKey] ??
      CategoryLabels[categoryItemType!] ??
      categoryItemType)
    : "...";

  // Ouvre la modale au lieu de soumettre directement
  const handleSaveClick = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSaveDialogOpen(true);
  };

  // Exécute la mutation après confirmation
  const executeSave = () => {
    setIsSaveDialogOpen(false);
    mutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="patch-policy-page">
        <Navbar />
        <TopHeader />

        <main className="dashboard-wrapper">
          <div className="policy-loading">
            <div className="policy-loading-spinner" aria-hidden="true" />

            <p>{translations.loading}</p>
          </div>
        </main>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="patch-policy-page">
        <Navbar />
        <TopHeader />

        <main className="dashboard-wrapper">
          <div className="policy-error">{translations.loadError}</div>
        </main>

        {generateToastContainer()}
      </div>
    );
  }

  return (
    <div className="patch-policy-page">
      <Navbar />
      <TopHeader />

      <main className="dashboard-wrapper">
        <div className="patch-policy-container">
          <div className="patch-policy-card">
            <div className="patch-policy-header">
              <h2>
                {translations.editTitle}: <span>{categoryName}</span>
              </h2>

              <p>{translations.editSubtitle}</p>
            </div>

            <form onSubmit={handleSaveClick}>
              <div className="patch-policy-grid">
                <div className="policy-input-group">
                  <label htmlFor="spending-limit">
                    {translations.fields.spendingLimit}
                  </label>

                  <input
                    id="spending-limit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.spending_limit ?? ""}
                    onChange={(event) =>
                      handleChange("spending_limit", event.target.value)
                    }
                    placeholder={translations.placeholders.noLimit}
                  />
                </div>

                <div className="policy-input-group">
                  <label htmlFor="max-without-receipt">
                    {translations.fields.maxWithoutReceipt}
                  </label>

                  <input
                    id="max-without-receipt"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.max_amount_without_receipt ?? ""}
                    onChange={(event) =>
                      handleChange(
                        "max_amount_without_receipt",
                        event.target.value,
                      )
                    }
                    placeholder={translations.placeholders.zero}
                  />
                </div>

                <div className="policy-input-group">
                  <label htmlFor="vat-rule-type">
                    {translations.fields.vatRuleType}
                  </label>

                  <select
                    id="vat-rule-type"
                    value={formData.vat_rule_type ?? "NONE"}
                    onChange={(event) =>
                      handleChange("vat_rule_type", event.target.value)
                    }
                  >
                    <option value="NONE">{translations.vatRules.none}</option>

                    <option value="MANUAL">
                      {translations.vatRules.manual}
                    </option>

                    <option value="SUM_LINES">
                      {translations.vatRules.sumLines}
                    </option>
                  </select>
                </div>

                <div className="policy-input-group">
                  <label htmlFor="policy-status">
                    {translations.fields.status}
                  </label>

                  <select
                    id="policy-status"
                    value={formData.is_active ? "true" : "false"}
                    onChange={(event) =>
                      handleChange("is_active", event.target.value === "true")
                    }
                  >
                    <option value="true">{translations.statuses.active}</option>

                    <option value="false">
                      {translations.statuses.inactive}
                    </option>
                  </select>
                </div>
              </div>

              <h3 className="policy-section-title">
                {translations.conditionsTitle}
              </h3>

              <div className="policy-toggles-container">
                <ToggleItem
                  label={translations.conditions.allowed}
                  value={Boolean(formData.allowed)}
                  onClick={() => handleToggle("allowed")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.requiresReceipt}
                  value={Boolean(formData.requires_receipt)}
                  onClick={() => handleToggle("requires_receipt")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowsVat}
                  value={Boolean(formData.allows_vat)}
                  onClick={() => handleToggle("allows_vat")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowsManualVat}
                  value={Boolean(formData.allows_manual_vat)}
                  onClick={() => handleToggle("allows_manual_vat")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.requiresComment}
                  value={Boolean(formData.requires_comment)}
                  onClick={() => handleToggle("requires_comment")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.requiresSupplierName}
                  value={Boolean(formData.requires_supplier_name)}
                  onClick={() => handleToggle("requires_supplier_name")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowsDistanceKm}
                  value={Boolean(formData.allows_distance_km)}
                  onClick={() => handleToggle("allows_distance_km")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowsFuelExpense}
                  value={Boolean(formData.allows_fuel_expense)}
                  onClick={() => handleToggle("allows_fuel_expense")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowCompanyCar}
                  value={Boolean(formData.allow_company_car)}
                  onClick={() => handleToggle("allow_company_car")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowPersonalCar}
                  value={Boolean(formData.allow_personal_car)}
                  onClick={() => handleToggle("allow_personal_car")}
                  disabled={mutation.isPending}
                />
              </div>

              <div className="patch-policy-actions">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="policy-btn-cancel"
                  disabled={mutation.isPending}
                >
                  {translations.cancel}
                </button>

                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="policy-btn-submit"
                >
                  {mutation.isPending
                    ? translations.saving
                    : translations.saveChanges}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Modale de confirmation de sauvegarde */}
      <ConfirmDialog
        isOpen={isSaveDialogOpen}
        title={t.common?.saveConfirmTitle || "Confirmer les modifications"}
        content={
          translations.saveConfirmContent ||
          "Voulez-vous vraiment enregistrer ces modifications pour cette politique ?"
        }
        cancelText={t.common?.cancel || "Annuler"}
        confirmText={translations.saveChanges || "Enregistrer"}
        onCancel={() => setIsSaveDialogOpen(false)}
        onConfirm={executeSave}
        variant="primary"
      />

      {generateToastContainer()}
    </div>
  );
}

export default PatchCompanyPolicy;
